import { Checkbox } from "@xola/ui-kit";
import clsx from "clsx";
import { isScheduled, SwitchField } from "common";
import { cloneDeep, isNil, set } from "lodash";
import React, { useState } from "react";
import { CancellationPreference, CancellationRefundMethod, CutoffStrategy, Experience, PaymentMethod } from "types";
import { NumberInlineValuePopover } from "../../Common/NumberInlineValuePopover";
import { TimePickerPopover } from "../../Common/TimePickerPopover";
import { StrategyPopover } from "./ReschedulePreferenceField";

interface CancellationPreferenceFieldProps {
    experience: Experience;
    value: CancellationPreference | null;
    errors?: any;
    onChange: (value: CancellationPreference | null) => void;
}

export const CancellationPreferenceField = ({
    experience,
    value: cancellationPreference,
    errors,
    onChange,
}: CancellationPreferenceFieldProps) => {
    const isEnabled = !isNil(cancellationPreference);
    const hasSchedules = isScheduled(experience);
    const hasRefund = isEnabled && (cancellationPreference?.refundPercentage ?? 0) > 0;
    const paymentMethods: CancellationRefundMethod[] = cancellationPreference?.paymentMethods ?? [
        PaymentMethod.CREDIT_CARD,
    ];

    const [isRefundPercentagePopoverOpen, setIsRefundPercentagePopoverOpen] = useState(false);

    const handleChange = (key: string, value: any) => {
        let currentValue;
        if (cancellationPreference) {
            currentValue = cloneDeep(cancellationPreference);
            set(currentValue, key, value);
        }

        onChange(currentValue ?? null);
    };

    const toggleField = (enabled: boolean) => {
        const defaultValue: CancellationPreference = {
            cutoff: { duration: 0, strategy: CutoffStrategy.ARRIVAL },
            refundPercentage: 0,
            paymentMethods: [PaymentMethod.CREDIT_CARD],
        };
        onChange(enabled ? defaultValue : null);
    };

    const handlePaymentMethodToggle = (method: CancellationRefundMethod, checked: boolean) => {
        const allowedMethods: CancellationRefundMethod[] = [PaymentMethod.CREDIT_CARD, PaymentMethod.STORE_CREDIT];
        const updated = checked ? [...paymentMethods, method] : paymentMethods.filter((m) => m !== method);
        handleChange(
            "paymentMethods",
            allowedMethods.filter((m) => updated.includes(m)),
        );
    };

    return (
        <div className="space-y-4">
            <SwitchField isChecked={isEnabled} onChange={toggleField}>
                Permit cancellations
                {isEnabled ? (
                    <span onClick={(e) => e.stopPropagation()}>
                        {hasSchedules && cancellationPreference?.cutoff.strategy !== CutoffStrategy.CHECK_IN && (
                            <>
                                <span className="ml-1">up to</span>
                                <TimePickerPopover
                                    showArrow
                                    value={cancellationPreference?.cutoff.duration ?? 0}
                                    classNames={{
                                        text: clsx(errors?.cutoff?.duration ? "!text-danger" : "!text-black"),
                                    }}
                                    onApply={(value) => handleChange("cutoff.duration", value)}
                                />
                            </>
                        )}
                        {hasSchedules ? (
                            <span>
                                <span className="mx-1">prior to</span>
                                <StrategyPopover
                                    value={cancellationPreference?.cutoff.strategy}
                                    onChange={(value) => handleChange("cutoff.strategy", value)}
                                />
                                time
                            </span>
                        ) : null}
                        <span className="mx-1">and offer a</span>
                        <NumberInlineValuePopover
                            isOpen={isRefundPercentagePopoverOpen}
                            error={errors?.refundPercentage}
                            text={`${cancellationPreference.refundPercentage ?? 0} %`}
                            value={cancellationPreference.refundPercentage}
                            onChange={(value) => handleChange("refundPercentage", value)}
                            onOpenChange={() => setIsRefundPercentagePopoverOpen(!isRefundPercentagePopoverOpen)}
                        />
                        refund
                    </span>
                ) : null}
            </SwitchField>

            {hasRefund ? (
                <div>
                    <p className={clsx("mb-2 font-bold", errors?.paymentMethods && "text-danger")}>
                        Payment Methods {errors?.paymentMethods ? `- ${errors.paymentMethods}` : null}
                    </p>
                    <div className="space-y-2">
                        <Checkbox
                            checked={paymentMethods.includes(PaymentMethod.CREDIT_CARD)}
                            label="Credit Card"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handlePaymentMethodToggle(PaymentMethod.CREDIT_CARD, e.target.checked)
                            }
                        />
                        <Checkbox
                            checked={paymentMethods.includes(PaymentMethod.STORE_CREDIT)}
                            label="Store credit"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                handlePaymentMethodToggle(PaymentMethod.STORE_CREDIT, e.target.checked)
                            }
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};
